import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { usePayRateByLocation } from 'queries/pulse/usePayRateByLocation'
import { PayRateByLocation as PayRateByLocationType } from 'api/pulse/payRateByLocation'
import { formatCurrency, formatPercentage } from 'lib/string'
import { InfiniteTable } from '../InfiniteTable'
import { usePayRateByLocationDownloadCsv } from 'queries/pulse/usePayRateByLocationDownloadCsv'
import { formatTimePeriodDate } from '../../utils'

const columnHelper = createColumnHelper<PayRateByLocationType[0]>()

export function PayRateByLocation() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    usePayRateByLocation(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    usePayRateByLocationDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  const columns = [
    columnHelper.accessor('shift_date', {
      header: 'Period',
      cell: (info) => (
        <Text>
          {formatTimePeriodDate(filters.timePeriod, info.getValue() as string)}
        </Text>
      ),
    }),
    columnHelper.accessor('company_name', {
      header: 'Location Name',
    }),
    columnHelper.accessor('position_type', {
      header: 'Position Type',
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor('avg_pay_rate_previous_period', {
      header: 'Avg Pay Rate (Previous)',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatCurrency(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('avg_pay_rate_current_period', {
      header: 'Avg Pay Rate (Current)',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatCurrency(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('change_in_pay_rate_current_period', {
      header: 'Pay Rate Change',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatPercentage(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('fill_rate_previous_period', {
      header: 'Fill Rate (Previous)',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatPercentage(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('fill_rate_current_period', {
      header: 'Fill Rate (Current)',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatPercentage(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('period_change_in_fill_rate_current_period', {
      header: 'Fill Rate Change',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatPercentage(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
  ]

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Change in Average Pay Rate - by Location
          </Text>
          <Text fontSize={2} color="lightText">
            Location comparative evaluation of YoY pay changes, pay premium vs.
            market-level pay average, and subsequent fill impact
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          data={flatData}
          columns={columns}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
